import { useEffect, useState, useMemo, useRef } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { getPublicGroup, recordView, getViewsSummary, getSocialSummary, toggleLike as apiToggleLike } from '../utils/api'
import { downloadMedia, downloadAll, filenameForMedia } from '../utils/media'
import Lightbox from '../Components/Lightbox'
import MapView from '../Components/MapView'
import ViewsModal from '../Components/ViewsModal'
import MemberUpload from '../Components/MemberUpload'
import { C, gradient, fontStack } from '../constant/theme'
import { getIsAdmin } from '../utils/session'

const safeFolder = (s) =>
  String(s || 'Day')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .trim() || 'Day'

export default function PublicGroup() {
  const { slug } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [lightbox, setLightbox] = useState(null)
  const [dl, setDl] = useState(null)
  const [summary, setSummary] = useState({ trip: 0, media: {} })
  const [social, setSocial] = useState({ likes: {}, comments: {} })
  const [viewTarget, setViewTarget] = useState(null)
  const [showUpload, setShowUpload] = useState(false)
  const [mobile, setMobile] = useState(window.innerWidth < 640)
  const tripRecorded = useRef('')
  const deepLinked = useRef(false)

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const reload = () => getPublicGroup(slug).then(setData).catch(() => {})

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    getPublicGroup(slug)
      .then((res) => active && setData(res))
      .catch((err) => active && setError(err?.response?.data?.message || 'Could not load this trip.'))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [slug])

  useEffect(() => {
    if (!data || tripRecorded.current === slug) return
    tripRecorded.current = slug
    let active = true
    ;(async () => {
      try { await recordView(slug, { targetType: 'trip' }) } catch {}
      try {
        const [sum, soc] = await Promise.all([getViewsSummary(slug), getSocialSummary(slug)])
        if (active) {
          setSummary({ trip: sum.trip || 0, media: sum.media || {} })
          setSocial({ likes: soc.likes || {}, comments: soc.comments || {} })
        }
      } catch {}
    })()
    return () => { active = false }
  }, [data, slug])

  const recordMediaView = (m) => {
    if (!m) return
    recordView(slug, { targetType: 'media', targetId: m._id }).catch(() => {})
    setSummary((s) => ({ ...s, media: { ...s.media, [m._id]: (s.media[m._id] || 0) + 1 } }))
  }

  const openLightbox = (idx) => {
    recordMediaView(flatMedia[idx])
    setLightbox(idx)
  }

  const navLightbox = (dir) => {
    if (lightbox == null || flatMedia.length === 0) return
    const ni = (lightbox + dir + flatMedia.length) % flatMedia.length
    recordMediaView(flatMedia[ni])
    setLightbox(ni)
  }

  const handleToggleLike = async (mediaId) => {
    const prev = social.likes[mediaId] || { count: 0, likedByMe: false }
    // Optimistic update
    setSocial((s) => ({
      ...s,
      likes: { ...s.likes, [mediaId]: { count: prev.likedByMe ? prev.count - 1 : prev.count + 1, likedByMe: !prev.likedByMe } },
    }))
    try {
      const res = await apiToggleLike(slug, mediaId)
      setSocial((s) => ({ ...s, likes: { ...s.likes, [mediaId]: { count: res.count, likedByMe: res.liked } } }))
    } catch {
      // Revert on failure
      setSocial((s) => ({ ...s, likes: { ...s.likes, [mediaId]: prev } }))
    }
  }

  const handleCommentAdded = (mediaId) => {
    setSocial((s) => ({ ...s, comments: { ...s.comments, [mediaId]: (s.comments[mediaId] || 0) + 1 } }))
  }

  const handleCommentDeleted = (mediaId) => {
    setSocial((s) => ({ ...s, comments: { ...s.comments, [mediaId]: Math.max((s.comments[mediaId] || 0) - 1, 0) } }))
  }

  const { flatMedia, indexOf } = useMemo(() => {
    if (!data) return { flatMedia: [], indexOf: {} }
    const days = [...data.days].sort((a, b) => a.order - b.order)
    const flat = []
    const idx = {}
    for (const day of days) {
      const events = data.events.filter((e) => e.dayId === day._id).sort((a, b) => a.order - b.order)
      for (const ev of events) {
        const ms = data.media.filter((m) => m.eventId === ev._id).sort((a, b) => a.order - b.order)
        for (const m of ms) {
          idx[m._id] = flat.length
          flat.push(m)
        }
      }
    }
    return { flatMedia: flat, indexOf: idx }
  }, [data])

  // Auto-open lightbox when navigating from a notification (?media=<id>)
  useEffect(() => {
    if (!data || deepLinked.current) return
    const mediaId = searchParams.get('media')
    if (!mediaId) return
    const idx = indexOf[mediaId]
    if (idx == null) return
    deepLinked.current = true
    setLightbox(idx)
    setSearchParams({}, { replace: true })
  }, [data, indexOf, searchParams, setSearchParams])

  const mapPoints = useMemo(() => {
    if (!data) return []
    return data.events
      .filter((e) => typeof e.location?.lat === 'number' && typeof e.location?.lng === 'number')
      .map((e) => ({ lat: e.location.lat, lng: e.location.lng, title: e.title, sub: e.location.name }))
  }, [data])

  const runDownload = async (id, list, zipName, pathFn) => {
    if (!list.length || dl) return
    setDl({ id, pct: 0 })
    try {
      await downloadAll(list, zipName, { pathFn, onProgress: (p) => setDl({ id, pct: Math.round(p * 100) }) })
    } catch (err) {
      window.dispatchEvent(new CustomEvent('api-toast', { detail: { message: err.message || 'Download failed.' } }))
    } finally {
      setDl(null)
    }
  }

  if (loading) return <Centered>Loading…</Centered>
  if (error || !data) return <Centered>{error || 'Not found.'}</Centered>

  const { group, days, events, media } = data
  const members = data.members || []
  const canUpload = !!data.canUpload
  const memberName = Object.fromEntries(members.map((m) => [m.email, m.name || m.email]))
  const sortedDays = [...days].sort((a, b) => a.order - b.order)

  const dayLabelForEvent = {}
  for (const d of days) {
    const label = d.title || d.date || 'Day'
    for (const ev of events.filter((e) => e.dayId === d._id)) dayLabelForEvent[ev._id] = label
  }

  const downloadTrip = () =>
    runDownload('trip', flatMedia, `${safeFolder(group.name)}.zip`, (m, i) => `${safeFolder(dayLabelForEvent[m.eventId])}/${String(i + 1).padStart(3, '0')}-${filenameForMedia(m)}`)

  const downloadDay = (day, dayMedia) => runDownload(day._id, dayMedia, `${safeFolder(group.name)} - ${safeFolder(day.title || day.date || 'Day')}.zip`)

  const dlLabel = (id, base) => (dl?.id === id ? `Zipping… ${dl.pct}%` : base)

  return (
    <div style={S.root}>
      {/* Hero */}
      <div style={{ ...S.hero, minHeight: mobile ? 200 : 320, backgroundImage: group.coverImage?.url ? `linear-gradient(180deg, rgba(13,27,42,.25), rgba(13,27,42,.78)), url(${group.coverImage.url})` : gradient }}>
        <div style={{ ...S.heroInner, padding: mobile ? '24px 16px' : '40px 24px' }}>
          <Link to="/" style={S.backLink}>← All trips</Link>
          <h1 style={{ ...S.heroTitle, fontSize: mobile ? 24 : 38 }}>{group.name}</h1>
          <div style={S.heroMeta}>
            {group.locationName && <span>📍 {group.locationName}</span>}
            {(group.startDate || group.endDate) && <span>🗓 {[group.startDate, group.endDate].filter(Boolean).join(' → ')}</span>}
          </div>
          {group.summary && <p style={S.heroSummary}>{group.summary}</p>}
        </div>
      </div>

      <div style={{ ...S.body, padding: mobile ? '20px 14px' : '32px 24px' }}>
        <div style={{ ...S.toolbar, flexWrap: 'wrap', gap: mobile ? 10 : 12 }}>
          <button style={S.viewsBig} onClick={() => setViewTarget({ type: 'trip', id: group.id, label: group.name })}>
            👁 {summary.trip} {summary.trip === 1 ? 'view' : 'views'}
          </button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canUpload && (
              <button style={S.addPhotosBtn} onClick={() => setShowUpload(true)} disabled={sortedDays.length === 0}>
                ＋ Add photos
              </button>
            )}
            {flatMedia.length > 0 && (
              <button style={{ ...S.dlAllBtn, ...(dl ? S.dlBusy : {}) }} onClick={downloadTrip} disabled={!!dl}>
                ⬇ {dlLabel('trip', `Download all (${flatMedia.length})`)}
              </button>
            )}
          </div>
        </div>

        {members.length > 0 && (
          <section style={{ marginBottom: 28 }}>
            <h2 style={S.sectionTitle}>Who joined</h2>
            <div style={S.memberChips}>
              {members.filter((m) => m.name).map((m) => (
                <span key={m.email} style={S.memberChip}>{m.name}</span>
              ))}
            </div>
          </section>
        )}

        <section style={{ marginBottom: 28 }}>
          <h2 style={S.sectionTitle}>Where we went</h2>
          <MapView points={mapPoints} />
        </section>

        {sortedDays.length === 0 && <p style={{ color: C.textSoft }}>This trip has no entries yet.</p>}

        {sortedDays.map((day) => {
          const dayEvents = events.filter((e) => e.dayId === day._id).sort((a, b) => a.order - b.order)
          const dayMedia = dayEvents.flatMap((ev) => media.filter((m) => m.eventId === ev._id).sort((a, b) => a.order - b.order))
          return (
            <section key={day._id} style={S.daySection}>
              <div style={S.dayHeader}>
                <div style={S.dayDot} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={S.dayTitle}>{day.title || 'Day'}</h2>
                  {day.date && <div style={S.dayDate}>{day.date}</div>}
                </div>
                {dayMedia.length > 0 && (
                  <button style={{ ...S.dlDayBtn, ...(dl ? S.dlBusy : {}) }} onClick={() => downloadDay(day, dayMedia)} disabled={!!dl}>
                    ⬇ {dlLabel(day._id, `Download day (${dayMedia.length})`)}
                  </button>
                )}
              </div>

              {day.members?.length > 0 && (
                <div style={S.dayMembers}>👥 {day.members.map((e) => memberName[e] || e).join(', ')}</div>
              )}

              <div style={S.events}>
                {dayEvents.map((ev) => {
                  const evMedia = media.filter((m) => m.eventId === ev._id).sort((a, b) => a.order - b.order)
                  return (
                    <div key={ev._id} style={S.event}>
                      <div style={S.eventHead}>
                        <h3 style={S.eventTitle}>{ev.title || 'Event'}</h3>
                        {ev.time && <span style={S.eventTime}>{ev.time}</span>}
                      </div>
                      {ev.location?.name && <div style={S.eventLoc}>📍 {ev.location.name}</div>}
                      {ev.description && <p style={S.eventDesc}>{ev.description}</p>}
                      {evMedia.length > 0 && (
                        <div style={{ ...S.grid, gridTemplateColumns: mobile ? 'repeat(auto-fill, minmax(90px, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))' }}>
                          {evMedia.map((m) => {
                            const likeData = social.likes[m._id] || { count: 0, likedByMe: false }
                            const commentCount = social.comments[m._id] || 0
                            return (
                              <div key={m._id} style={S.mediaItem}>
                                <div style={S.tile} onClick={() => openLightbox(indexOf[m._id])}>
                                  {m.type === 'video' ? (
                                    <video src={m.url} style={S.tileMedia} muted />
                                  ) : (
                                    <img src={m.url} alt={m.caption || ''} style={S.tileMedia} loading="lazy" />
                                  )}
                                  {m.type === 'video' && <span style={S.play}>▶</span>}
                                  <button
                                    style={S.tileDl}
                                    title="Download"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      downloadMedia(m).catch(() => window.dispatchEvent(new CustomEvent('api-toast', { detail: { message: 'Download failed.' } })))
                                    }}
                                  >
                                    ⬇
                                  </button>
                                </div>

                                {/* Social footer */}
                                <div style={S.mediaFoot}>
                                  <span style={S.posterSmall}>{m.postedBy || <span style={S.posterEmpty}>~</span>}</span>
                                  <div style={S.socialRow}>
                                    <button
                                      style={{ ...S.socialBtn, color: likeData.likedByMe ? '#e0245e' : C.textSoft }}
                                      title={likeData.likedByMe ? 'Unlike' : 'Like'}
                                      onClick={(e) => { e.stopPropagation(); handleToggleLike(m._id) }}
                                    >
                                      {likeData.likedByMe ? '❤️' : '🤍'} {likeData.count > 0 ? likeData.count : ''}
                                    </button>
                                    <button
                                      style={S.socialBtn}
                                      title="View comments"
                                      onClick={(e) => { e.stopPropagation(); openLightbox(indexOf[m._id]) }}
                                    >
                                      💬 {commentCount > 0 ? commentCount : ''}
                                    </button>
                                    <button
                                      style={S.viewsSmall}
                                      title="Who viewed"
                                      onClick={(e) => { e.stopPropagation(); setViewTarget({ type: 'media', id: m._id, label: m.caption || filenameForMedia(m) }) }}
                                    >
                                      👁 {summary.media[m._id] || 0}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )
        })}

        <footer style={S.footer}>Made with 🧭 Tour Timeline</footer>
      </div>

      <Lightbox
        items={flatMedia}
        index={lightbox}
        viewsByMedia={summary.media}
        onClose={() => setLightbox(null)}
        onNav={navLightbox}
        slug={slug}
        socialData={social.likes}
        commentCounts={social.comments}
        onToggleLike={handleToggleLike}
        onCommentAdded={handleCommentAdded}
        onCommentDeleted={handleCommentDeleted}
      />

      {viewTarget && <ViewsModal slug={slug} target={viewTarget} onClose={() => setViewTarget(null)} />}

      {showUpload && (
        <MemberUpload slug={slug} days={sortedDays} isAdmin={getIsAdmin()} onClose={() => setShowUpload(false)} onUploaded={reload} />
      )}
    </div>
  )
}

function Centered({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, fontFamily: fontStack, color: C.text, padding: 24 }}>
      {children}
    </div>
  )
}

const S = {
  root: { minHeight: '100vh', background: C.bg, fontFamily: fontStack },
  hero: { minHeight: 320, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'flex-end' },
  heroInner: { maxWidth: 900, margin: '0 auto', width: '100%', padding: '40px 24px', color: '#fff' },
  heroTitle: { fontSize: 38, fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px', textShadow: '0 2px 12px rgba(0,0,0,.4)' },
  heroMeta: { display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 14, color: 'rgba(255,255,255,0.9)' },
  heroSummary: { fontSize: 15, color: 'rgba(255,255,255,0.88)', maxWidth: 620, lineHeight: 1.6, marginBottom: 0 },
  body: { maxWidth: 900, margin: '0 auto', padding: '32px 24px' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 },
  viewsBig: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', color: C.navy2, border: `1.5px solid ${C.cardBorder}`, borderRadius: 10, padding: '8px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  addPhotosBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1f7a48', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  memberChips: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  memberChip: { fontSize: 13, fontWeight: 600, background: '#d6deea', color: C.navy2, padding: '5px 12px', borderRadius: 20 },
  dayMembers: { fontSize: 13, color: C.textSoft, marginBottom: 12, marginLeft: 24 },
  dlAllBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: C.navy2, color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  dlDayBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', color: C.navy2, border: `1.5px solid ${C.cardBorder}`, borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  dlBusy: { opacity: 0.6, cursor: 'default' },
  sectionTitle: { fontSize: 18, fontWeight: 800, color: C.text, margin: '0 0 14px' },
  daySection: { marginBottom: 36 },
  dayHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  dayDot: { width: 12, height: 12, borderRadius: '50%', background: C.navy2, boxShadow: '0 0 0 4px rgba(27,53,88,.15)' },
  dayTitle: { fontSize: 20, fontWeight: 800, color: C.text, margin: 0 },
  dayDate: { fontSize: 13, color: C.textSoft },
  events: { display: 'flex', flexDirection: 'column', gap: 16, borderLeft: `2px solid ${C.cardBorder}`, marginLeft: 5, paddingLeft: 22 },
  event: { background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 18 },
  eventHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 },
  eventTitle: { fontSize: 16, fontWeight: 700, color: C.text, margin: 0 },
  eventTime: { fontSize: 13, color: C.textSoft, fontWeight: 600 },
  eventLoc: { fontSize: 13, color: C.textSoft, marginTop: 4 },
  eventDesc: { fontSize: 14.5, color: '#33404f', lineHeight: 1.6, margin: '10px 0 0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginTop: 14, alignItems: 'start' },
  mediaItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  mediaFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 },
  posterSmall: { fontSize: 11, color: C.textSoft, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  posterEmpty: { color: C.muted, fontStyle: 'italic' },
  socialRow: { display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 },
  socialBtn: { background: 'transparent', border: 'none', color: C.textSoft, fontSize: 12, cursor: 'pointer', padding: '2px 3px', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 1, lineHeight: 1 },
  viewsSmall: { flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3, background: 'transparent', border: 'none', color: C.navy2, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '2px 3px' },
  tile: { position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: '#e4e9f1' },
  tileDl: { position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(20,30,45,.62)', color: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  tileMedia: { width: '100%', height: '100%', objectFit: 'cover' },
  play: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 24, textShadow: '0 2px 8px rgba(0,0,0,.6)', pointerEvents: 'none' },
  footer: { textAlign: 'center', color: C.muted, fontSize: 13, padding: '30px 0 10px' },
  backLink: { display: 'inline-block', marginBottom: 14, color: 'rgba(255,255,255,0.92)', textDecoration: 'none', fontSize: 13.5, fontWeight: 600 },
}
